import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Department, ApiDepartmentItem, ApiDepartmentResponse } from '../models/approval-flow-definition.model';
import { ApprovalService } from './approval.service';
import { environment } from '../../../../services/global-environment';

@Injectable({
  providedIn: 'root'
})
export class DepartmentService {
  private apiUrl = `${environment.apiUrl}/api/departments`;
  // 開發環境使用模擬資料，設為 false 時將使用實際 API 呼叫

  constructor(
    private http: HttpClient,
    private approvalService: ApprovalService
  ) { }

  getAllDepartments(): Observable<Department[]> {
    return this.getDepartmentsFromAPI();
  }

  // 新增：從實際 API 獲取部門資料
  getDepartmentsFromAPI(): Observable<Department[]> {
    const companyid = environment.companyid;
    const apiUrl = environment.apiUrl;

    console.log('getDepartmentsFromAPI: companyid =', companyid, 'apiUrl =', apiUrl);

    if (!companyid || !apiUrl) {
      console.warn('Company ID or API URL not found in environment, returning empty array');
      return of([]);
    }

    const url = `${apiUrl}/api/departments/${companyid}/ALL`;
    console.log('Fetching departments from:', url);

    return this.http.get<ApiDepartmentResponse>(url).pipe(
      map(response => {
        console.log('API Response for departments:', response);

        if (!response.success || !response.data) {
          console.warn('Invalid API response structure, falling back to mock data');
          throw new Error('Invalid API response');
        }

        // 從根節點開始解析部門資料
        const departments = this.extractDepartmentsWithHierarchy(response.data);
        console.log('Extracted departments with hierarchy:', departments);
        return departments;
      }),
      catchError(error => {
        console.warn('Error fetching departments from API, falling back to mock data:', error);
        // 發生錯誤時回退到 mock 資料
        return [];
      })
    );
  }

  // 遞迴解析部門資料，建立階層結構，只提取 company 和 department 類型的項目
  private extractDepartmentsWithHierarchy(rootItem: ApiDepartmentItem, level: number = 0, parentId?: string): Department[] {
    const departments: Department[] = [];

    // 如果是公司或部門類型，則加入結果
    if (rootItem.type === 'company' || rootItem.type === 'department') {
      departments.push({
        id: rootItem.id,
        name: rootItem.name,
        level: level,
        parentId: parentId
      });
    }

    // 遞迴處理子項目
    if (rootItem.children && rootItem.children.length > 0) {
      rootItem.children.forEach(child => {
        if (child.type === 'company' || child.type === 'department') {
          // 只有當子項目是公司或部門時才遞迴處理
          const childDepartments = this.extractDepartmentsWithHierarchy(
            child,
            rootItem.type === 'company' || rootItem.type === 'department' ? level + 1 : level,
            rootItem.type === 'company' || rootItem.type === 'department' ? rootItem.id : parentId
          );
          departments.push(...childDepartments);
        }
      });
    }

    return departments;
  }

  // 新增：取得指定部門的上級部門 ID
  getParentDepartmentId(departmentId: string): Observable<string | null> {
    const companyid = environment.companyid;
    const apiUrl = environment.apiUrl;

    if (!companyid || !apiUrl || !departmentId) {
      console.warn('Company ID, API URL, or Department ID not provided');
      return of(null);
    }

    const url = `${apiUrl}/api/departments/${companyid}/ALL`;
    console.log('Fetching organization data to find parent department for:', departmentId);

    return this.http.get<ApiDepartmentResponse>(url).pipe(
      map(response => {
        if (!response.success || !response.data) {
          console.warn('Invalid API response structure');
          return null;
        }

        // 在組織圖中搜尋指定部門的父層 ID
        const parentId = this.findParentDepartmentId(response.data, departmentId);
        console.log(`Found parent department ID for ${departmentId}:`, parentId);
        return parentId;
      }),
      catchError(error => {
        console.error('Error fetching organization data for parent department:', error);
        return of(null);
      })
    );
  }

  // 遞迴搜尋指定部門的父層 ID
  private findParentDepartmentId(node: ApiDepartmentItem, targetDepartmentId: string, currentParentId?: string): string | null {
    // 如果當前節點就是目標部門，返回其父層 ID
    if (node.type === 'department' && node.id === targetDepartmentId) {
      return currentParentId || null;
    }

    // 如果有子節點，遞迴搜尋
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        // 只有 department 或 company 類型才能作為父層
        const nextParentId = (node.type === 'department' || node.type === 'company') ? node.id : currentParentId;

        const result = this.findParentDepartmentId(child, targetDepartmentId, nextParentId);
        if (result !== null) {
          return result;
        }
      }
    }

    return null;
  }
}
